/**
 * Product Carousel section: navigation, slide accessibility, and live region updates.
 * Swiper init is handled by s-carousel in theme.js.
 */

(function () {
  'use strict';

  function isDisabled(button) {
    return button?.getAttribute('aria-disabled') === 'true';
  }

  function setButtonDisabledState(button, disabled) {
    if (!button) return;

    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    button.tabIndex = disabled ? -1 : 0;
  }

  function updateNavButtonStates(swiper, prevBtn, nextBtn) {
    if (!swiper) return;

    if (prevBtn) {
      const atStart = swiper.isBeginning;
      prevBtn.classList.toggle('product-carousel__nav-btn--at-start', atStart);
      setButtonDisabledState(prevBtn, atStart);
    }

    if (nextBtn) {
      const atEnd = swiper.isEnd;
      nextBtn.classList.toggle('product-carousel__nav-btn--at-end', atEnd);
      setButtonDisabledState(nextBtn, atEnd);
    }
  }

  function updateSlideAccessibility(carouselWrapper, swiper) {
    if (!swiper?.slides) return;

    const slideCount =
      parseInt(carouselWrapper.dataset.slideCount, 10) || swiper.slides.length;

    swiper.slides.forEach(function (slide) {
      if (!(slide instanceof HTMLElement)) return;

      const visible = slide.classList.contains('swiper-slide-visible');

      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');

      if (visible) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });

    const liveRegion = carouselWrapper.querySelector('[data-product-carousel-status]');
    if (!liveRegion) return;

    const activeIndex =
      typeof swiper.activeIndex === 'number' ? swiper.activeIndex + 1 : 1;
    const template = carouselWrapper.dataset.slideStatusTemplate;

    liveRegion.textContent = template
      ? template
          .replace('__INDEX__', String(activeIndex))
          .replace('__COUNT__', String(slideCount))
      : 'Slide ' + activeIndex + ' of ' + slideCount;
  }

  function bindSwiper(section, carouselWrapper, carouselEl, prevBtn, nextBtn) {
    const swiper = carouselEl.swiper;
    if (!swiper || carouselWrapper.dataset.a11yInit === 'true') return;

    carouselWrapper.dataset.a11yInit = 'true';

    const sync = function () {
      updateNavButtonStates(swiper, prevBtn, nextBtn);
      updateSlideAccessibility(carouselWrapper, swiper);
    };

    sync();
    swiper.on('slideChange', sync);
    swiper.on('resize', sync);
    swiper.on('breakpoint', sync);

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (isDisabled(prevBtn) || !carouselEl.swiper) return;
        carouselEl.swiper.slidePrev();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (isDisabled(nextBtn) || !carouselEl.swiper) return;
        carouselEl.swiper.slideNext();
      });
    }
  }

  function initProductCarousel(section) {
    if (!section || section.dataset.productCarouselInited) return;
    section.dataset.productCarouselInited = 'true';

    const carouselWrapper = section.querySelector('s-carousel');
    const carouselEl = section.querySelector('[js-carousel]');
    const prevBtn = section.querySelector('[data-product-carousel-prev]');
    const nextBtn = section.querySelector('[data-product-carousel-next]');

    if (!carouselEl || !carouselWrapper) return;

    if (prevBtn) setButtonDisabledState(prevBtn, true);

    function handleReady(event) {
      if (event.detail?.swiper) {
        bindSwiper(section, carouselWrapper, carouselEl, prevBtn, nextBtn);
      }
    }

    if (carouselEl.swiper) {
      bindSwiper(section, carouselWrapper, carouselEl, prevBtn, nextBtn);
    } else if (carouselWrapper) {
      carouselWrapper.addEventListener('carousel:ready', handleReady);
    }
  }

  function initAll(container) {
    const root = container || document;
    root.querySelectorAll('[data-product-carousel]').forEach(initProductCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('[data-product-carousel]');
    if (!section) return;

    delete section.dataset.productCarouselInited;

    const carouselWrapper = section.querySelector('s-carousel');
    if (carouselWrapper) {
      delete carouselWrapper.dataset.a11yInit;
    }

    const carouselEl = section.querySelector('[js-carousel].swiper-initialized');
    if (carouselEl?.swiper?.destroy) {
      carouselEl.swiper.destroy(true, true);
    }
  });
})();
